import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material';

import Autocomplete from '~/components/ui/Autocomplete';
import { Button } from '~/components/ui/button';
import Tabs from '~/components/ui/Tabs';
import TextInput from '~/components/ui/TextInput';
import { DESKTOP_MIN_WIDTH, GOOGLE_MAP_KEY, TABLET_MIN_WIDTH } from '~/constants/common';
import { LOCATION_TYPE } from '~/constants/vCard';
import { COLORS } from '~/styles/colors';
import { LocationType } from '~/types/qrCode';
import { isMaxLength } from '~/utils/common';
import { parsePlaceDetails } from '~/utils/google-maps/adress';

type BuilderLocationBlockProps = {
  locationType: LocationType;
  onChangeLocationType: (newType: LocationType) => void;
  fieldNames: {
    complete: {
      searchAddress: string;
      street: string;
      number: string;
      postalCode: string;
      city: string;
      state: string;
      country: string;
    };
    url: string;
    coordinates: {
      longitude: string;
      latitude: string;
    };
  };
  onDeleteAddress: () => void;
  onApplyNewAddress: () => void;
  isManuallyAddress: boolean;
  onChangeIsManuallyAddress: (value: boolean) => void;
};

export default function BuilderLocationBlock({
  locationType,
  onChangeLocationType,
  fieldNames,
  onDeleteAddress,
  onApplyNewAddress,
  isManuallyAddress,
  onChangeIsManuallyAddress,
}: BuilderLocationBlockProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_MIN_WIDTH));
  const { control, setValue } = useFormContext();

  // TODO use for future Location visibility Select (commented out below)
  // const locationTabs = [
  //   {
  //     value: 'HIDE_ADDRESS_LINK',
  //     label: t('other:hideLink'),
  //   },
  //   {
  //     value: 'SHOW_ADDRESS_LINK',
  //     label: t('other:showLink'),
  //   },
  //   {
  //     value: 'SHOW_MAP',
  //     label: t('other:showMap'),
  //   },
  // ];

  const tabsList = [
    { value: LOCATION_TYPE.FULL, label: t('template5:complete') },
    { value: LOCATION_TYPE.URL, label: t('template5:url') },
    { value: LOCATION_TYPE.COORDINATES, label: t('template5:coordinate') },
  ];

  const { placesService, placePredictions, getPlacePredictions } = usePlacesService({
    apiKey: GOOGLE_MAP_KEY,
  });

  const handleTabItemClick = (_, newValue: LocationType) => {
    onChangeLocationType(newValue);
  };

  const handlePreselectPlaceDetails = (placeDetails: any) => {
    onChangeIsManuallyAddress(true);

    const { city, country, postalCode, state, street, number } = parsePlaceDetails(placeDetails);

    setValue(fieldNames.complete.city, city);
    setValue(fieldNames.complete.postalCode, postalCode);
    setValue(fieldNames.complete.country, country);
    setValue(fieldNames.complete.number, number);
    setValue(fieldNames.complete.street, street);
    setValue(fieldNames.complete.state, state);
  };

  const handleManually = () => {
    onChangeIsManuallyAddress(!isManuallyAddress);
    if (isManuallyAddress) {
      setValue(fieldNames.complete.city, '');
      setValue(fieldNames.complete.postalCode, '');
      setValue(fieldNames.complete.country, '');
      setValue(fieldNames.complete.number, '');
      setValue(fieldNames.complete.street, '');
      setValue(fieldNames.complete.state, '');
    }
    onDeleteAddress();
  };

  return (
    <>
      {isDesktop ? (
        <StyledTabs>
          {tabsList.map(({ label, value }) => {
            const isSelected = value === locationType;

            return (
              <StyledTab
                key={value}
                $isSelected={isSelected}
                onClick={() => {
                  onChangeLocationType(value);
                }}
              >
                {label}
              </StyledTab>
            );
          })}
        </StyledTabs>
      ) : (
        <Tabs tabs={tabsList} value={locationType} onChange={handleTabItemClick} />
      )}

      {locationType === LOCATION_TYPE.FULL && (
        <>
          {/* TODO add controller and add condition to render via prop */}
          {/*<Select*/}
          {/*  label={t('template5:locationOpt')}*/}
          {/*  items={locationTabs}*/}
          {/*  value={locationVisibility}*/}
          {/*  onChange={(e) => {*/}
          {/*    setLocationVisibility(e.target.value);*/}
          {/*    handleUpdateField({ field: 'location_visibility', value: e.target.value });*/}
          {/*  }}*/}
          {/*  fullWidth*/}
          {/*/>*/}
          <StyledAutocompleteContainer>
            <Controller
              control={control}
              name={fieldNames.complete.searchAddress}
              render={({ field: { value, onChange } }) => {
                return (
                  <Autocomplete
                    id={fieldNames.complete.searchAddress}
                    freeSolo
                    fullWidth
                    disabled={isManuallyAddress}
                    disableClearable
                    options={placePredictions.map((option) => option.description)}
                    onChange={(_, newValue) => {
                      const selectedPlace = placePredictions.find((item) => item.description === newValue);
                      onChange(selectedPlace.description);

                      placesService?.getDetails(
                        {
                          placeId: selectedPlace.place_id,
                        },
                        (placeDetails) => handlePreselectPlaceDetails(placeDetails),
                      );

                      onApplyNewAddress();
                    }}
                    value={value}
                    placeholder={`${t('common:startTypingHere')}...`}
                    label={t('template5:searchAddress')}
                    onTextInputChange={(evt) => {
                      getPlacePredictions({
                        input: evt.target.value,
                      });
                      onChange(evt);
                    }}
                  />
                );
              }}
            />

            <Button
              sx={{ whiteSpace: 'nowrap' }}
              type="primary-outlined"
              onClick={handleManually}
              size="xs"
              color={isManuallyAddress ? COLORS.error : undefined}
              borderColor={isManuallyAddress ? COLORS.error : undefined}
            >
              {!isManuallyAddress ? t('template5:manual') : t('template5:delete_address')}
            </Button>
          </StyledAutocompleteContainer>
          {isManuallyAddress && (
            <>
              <StyledManuallyBlock>
                <StyledManuallyHeaderLeft>
                  <Controller
                    control={control}
                    name={fieldNames.complete.street}
                    render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
                      const reachedMaxLengthError = isMaxLength(value, 70);

                      return (
                        <TextInput
                          id={fieldNames.complete.street}
                          value={value}
                          onChange={onChange}
                          onBlur={onBlur}
                          error={!!error?.message}
                          helperText={error?.message || reachedMaxLengthError}
                          fullWidth
                          label={t('template5:street')}
                          placeholder={t('common:typeHere')}
                          inputProps={{ maxLength: 70 }}
                        />
                      );
                    }}
                  />
                </StyledManuallyHeaderLeft>
                <StyledManuallyHeaderRight>
                  <Controller
                    control={control}
                    name={fieldNames.complete.number}
                    render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
                      const reachedMaxLengthError = isMaxLength(value, 40);

                      return (
                        <TextInput
                          id={fieldNames.complete.number}
                          value={value}
                          onChange={onChange}
                          onBlur={onBlur}
                          error={!!error?.message}
                          helperText={error?.message || reachedMaxLengthError}
                          fullWidth
                          label={t('template5:strNumber')}
                          placeholder={t('common:typeHere')}
                          inputProps={{ maxLength: 40, inputMode: 'numeric', pattern: '[0-9]*' }}
                        />
                      );
                    }}
                  />
                  <Controller
                    control={control}
                    name={fieldNames.complete.postalCode}
                    render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
                      const reachedMaxLengthError = isMaxLength(value, 40);

                      return (
                        <TextInput
                          id={fieldNames.complete.postalCode}
                          value={value}
                          onChange={onChange}
                          onBlur={onBlur}
                          error={!!error?.message}
                          helperText={error?.message || reachedMaxLengthError}
                          fullWidth
                          label={t('template5:postCode')}
                          placeholder={t('common:typeHere')}
                          inputProps={{ maxLength: 40 }}
                        />
                      );
                    }}
                  />
                </StyledManuallyHeaderRight>
              </StyledManuallyBlock>
              <StyledManuallyBlock>
                <Controller
                  control={control}
                  name={fieldNames.complete.city}
                  render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
                    const reachedMaxLengthError = isMaxLength(value, 70);

                    return (
                      <TextInput
                        id={fieldNames.complete.city}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        error={!!error?.message}
                        helperText={error?.message || reachedMaxLengthError}
                        fullWidth
                        label={t('template5:city')}
                        placeholder={t('common:typeHere')}
                        inputProps={{ maxLength: 70 }}
                      />
                    );
                  }}
                />
                <Controller
                  control={control}
                  name={fieldNames.complete.state}
                  render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
                    const reachedMaxLengthError = isMaxLength(value, 70);

                    return (
                      <TextInput
                        id={fieldNames.complete.state}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        error={!!error?.message}
                        helperText={error?.message || reachedMaxLengthError}
                        fullWidth
                        label={t('template5:state')}
                        placeholder={t('common:typeHere')}
                        inputProps={{ maxLength: 70 }}
                      />
                    );
                  }}
                />
              </StyledManuallyBlock>
              <Controller
                control={control}
                name={fieldNames.complete.country}
                render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
                  const reachedMaxLengthError = isMaxLength(value, 70);

                  return (
                    <TextInput
                      id={fieldNames.complete.country}
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      error={!!error?.message}
                      helperText={error?.message || reachedMaxLengthError}
                      fullWidth
                      label={t('template5:country')}
                      placeholder={t('common:typeHere')}
                      inputProps={{ maxLength: 70 }}
                    />
                  );
                }}
              />
            </>
          )}
        </>
      )}
      {locationType === LOCATION_TYPE.URL && (
        <Controller
          control={control}
          name={fieldNames.url}
          render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
            return (
              <TextInput
                id={fieldNames.url}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                error={!!error?.message}
                helperText={error?.message}
                fullWidth
                label={t('labels:url')}
                placeholder={t('template5:url')}
              />
            );
          }}
        />
      )}
      {locationType === LOCATION_TYPE.COORDINATES && (
        <StyledCoordinatesContainer>
          <Controller
            control={control}
            name={fieldNames.coordinates.latitude}
            render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
              const reachedMaxLengthError = isMaxLength(value, 50);

              return (
                <TextInput
                  id={fieldNames.coordinates.latitude}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={!!error?.message}
                  helperText={error?.message || reachedMaxLengthError}
                  fullWidth
                  label={t('template5:latitude')}
                  placeholder={`${t('common:eg')} 00.000000`}
                  inputProps={{ maxLength: 50 }}
                />
              );
            }}
          />
          <Controller
            control={control}
            name={fieldNames.coordinates.longitude}
            render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
              const reachedMaxLengthError = isMaxLength(value, 50);

              return (
                <TextInput
                  id={fieldNames.coordinates.longitude}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={!!error?.message}
                  helperText={error?.message || reachedMaxLengthError}
                  fullWidth
                  label={t('template5:longitude')}
                  placeholder={`${t('common:eg')} 00.000000`}
                  inputProps={{ maxLength: 50 }}
                />
              );
            }}
          />
        </StyledCoordinatesContainer>
      )}
    </>
  );
}

export const StyledTabs = styled('div')(() => {
  return {
    width: '100%',
    borderRadius: '8px',
    display: 'flex',
    padding: '8px 32px',
    gap: '20px',
    border: `1px solid ${COLORS.grey200}`,
    marginBottom: '16px',
    backgroundColor: COLORS.white,
  };
});

export const StyledTab = styled('button')<{ $isSelected: boolean }>(({ $isSelected }) => {
  return {
    borderRadius: '8px',
    flex: 1,
    cursor: 'pointer',
    border: `1px solid ${$isSelected ? COLORS.textMain : COLORS.grey250}`,
    color: $isSelected ? COLORS.textMain : COLORS.grey250,
    fontSize: '14px',
    fontWeight: 500,
    padding: '4px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: '19px',
    background: 'none',
  };
});

export const StyledAutocompleteContainer = styled('div')(({ theme }) => {
  return {
    display: 'flex',
    gap: '8px',
    alignItems: 'end',
    [theme.breakpoints.down(DESKTOP_MIN_WIDTH)]: {
      flexDirection: 'column',
      gap: '12px',
      alignItems: 'center',
    },
  };
});

export const StyledCoordinatesContainer = styled('div')(({ theme }) => {
  return {
    display: 'flex',
    gap: '16px',
    [theme.breakpoints.down(DESKTOP_MIN_WIDTH)]: {
      flexDirection: 'column',
    },
  };
});

export const StyledManuallyBlock = styled('div')(({ theme }) => {
  return {
    display: 'flex',
    gap: '16px',
    marginTop: '16px',
    marginBottom: '16px',
    [theme.breakpoints.down(TABLET_MIN_WIDTH)]: {
      flexDirection: 'column',
    },
  };
});

export const StyledManuallyHeaderLeft = styled('div')(({ theme }) => {
  return {
    flex: 1,
    [theme.breakpoints.down(TABLET_MIN_WIDTH)]: {
      flex: 'none',
    },
  };
});

export const StyledManuallyHeaderRight = styled('div')(({ theme }) => {
  return {
    display: 'flex',
    gap: '16px',
    flex: 1,
    [theme.breakpoints.down(DESKTOP_MIN_WIDTH)]: {
      flex: 2,
    },
    [theme.breakpoints.down(TABLET_MIN_WIDTH)]: {
      flexDirection: 'column',
      flex: 'none',
    },
  };
});
